import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { z } from "zod";
import {
  Search,
  Loader2,
  X,
  RefreshCw,
  AlertCircle,
  Users,
  MapPin,
  Mail,
  Phone,
  FileText,
  Calendar,
  Building2,
  ChevronRight,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useTRPC, useTRPCClient, RouterOutputs } from "@/trpc";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

type LeadListItem = RouterOutputs["lead"]["list"]["items"][number];
type LeadDetail = RouterOutputs["lead"]["getById"];
type LeadListPage = RouterOutputs["lead"]["list"];

const ALL_SOURCES_VALUE = "all";
const ALL_STATES_VALUE = "all";

const AdminLeadsPage = () => {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Local state for filters
  const [searchQuery, setSearchQuery] = useState(search.search ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(search.search ?? "");
  const [selectedProviderTypes, setSelectedProviderTypes] = useState<
    Array<{ id: string; name: string }>
  >(
    search.healthcareProfessionalTypeIds
      ? search.healthcareProfessionalTypeIds.map((id) => ({ id, name: "" }))
      : [],
  );
  const [selectedState, setSelectedState] = useState<string>(
    search.state ?? ALL_STATES_VALUE,
  );
  const [selectedLicenseState, setSelectedLicenseState] = useState<string>(
    search.licenseState ?? ALL_STATES_VALUE,
  );
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>(
    search.specialtyCodes ?? [],
  );
  const [selectedSource, setSelectedSource] = useState<string>(
    search.source ?? ALL_SOURCES_VALUE,
  );

  // Detail panel state
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL when filters change
  const updateSearchParams = useCallback(() => {
    const nextSearch: Record<string, unknown> = {};

    if (debouncedSearch) {
      nextSearch.search = debouncedSearch;
    }
    if (selectedProviderTypes.length > 0) {
      nextSearch.healthcareProfessionalTypeIds = selectedProviderTypes.map(
        (pt) => pt.id,
      );
    }
    if (selectedState && selectedState !== ALL_STATES_VALUE) {
      nextSearch.state = selectedState;
    }
    if (selectedLicenseState && selectedLicenseState !== ALL_STATES_VALUE) {
      nextSearch.licenseState = selectedLicenseState;
    }
    if (selectedSpecialties.length > 0) {
      nextSearch.specialtyCodes = selectedSpecialties;
    }
    if (selectedSource && selectedSource !== ALL_SOURCES_VALUE) {
      nextSearch.source = selectedSource;
    }

    navigate({
      search: () => nextSearch,
      replace: true,
    });
  }, [
    debouncedSearch,
    selectedProviderTypes,
    selectedState,
    selectedLicenseState,
    selectedSpecialties,
    selectedSource,
    navigate,
  ]);

  useEffect(() => {
    updateSearchParams();
  }, [updateSearchParams]);

  // Fetch sources for filter dropdown
  const { data: sources } = useQuery(trpc.lead.getSources.queryOptions());

  // Fetch states for filter dropdown
  const { data: states } = useQuery(trpc.lead.getStates.queryOptions());

  // Fetch license states for filter dropdown
  const { data: licenseStates } = useQuery(
    trpc.lead.getLicenseStates.queryOptions(),
  );

  // Infinite query for leads list
  const leadsQuery = useInfiniteQuery<
    LeadListPage,
    Error,
    InfiniteData<LeadListPage>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: [
      "lead.list",
      debouncedSearch,
      selectedProviderTypes.map((pt) => pt.id),
      selectedState,
      selectedLicenseState,
      selectedSpecialties,
      selectedSource,
    ],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      trpcClient.lead.list.query({
        cursor: pageParam ?? undefined,
        search: debouncedSearch || undefined,
        healthcareProfessionalTypeIds:
          selectedProviderTypes.length > 0
            ? selectedProviderTypes.map((pt) => pt.id)
            : undefined,
        state:
          selectedState && selectedState !== ALL_STATES_VALUE
            ? selectedState
            : undefined,
        licenseState:
          selectedLicenseState && selectedLicenseState !== ALL_STATES_VALUE
            ? selectedLicenseState
            : undefined,
        specialtyCodes:
          selectedSpecialties.length > 0 ? selectedSpecialties : undefined,
        source:
          selectedSource && selectedSource !== ALL_SOURCES_VALUE
            ? selectedSource
            : undefined,
      }),
  });

  // Fetch selected lead details
  const leadDetailQuery = useQuery({
    ...trpc.lead.getById.queryOptions({ id: selectedLeadId ?? "" }),
    enabled: Boolean(selectedLeadId),
  });

  // Flatten leads from all pages
  const leads = useMemo(() => {
    return leadsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  }, [leadsQuery.data]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          leadsQuery.hasNextPage &&
          !leadsQuery.isFetchingNextPage
        ) {
          leadsQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [leadsQuery.hasNextPage, leadsQuery.isFetchingNextPage]);

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setSelectedProviderTypes([]);
    setSelectedState(ALL_STATES_VALUE);
    setSelectedLicenseState(ALL_STATES_VALUE);
    setSelectedSpecialties([]);
    setSelectedSource(ALL_SOURCES_VALUE);
  };

  const hasActiveFilters =
    searchQuery ||
    selectedProviderTypes.length > 0 ||
    (selectedState && selectedState !== ALL_STATES_VALUE) ||
    (selectedLicenseState && selectedLicenseState !== ALL_STATES_VALUE) ||
    selectedSpecialties.length > 0 ||
    (selectedSource && selectedSource !== ALL_SOURCES_VALUE);

  // Export CSV mutation
  const exportMutation = useMutation({
    mutationFn: () =>
      trpcClient.lead.exportCsv.mutate({
        search: debouncedSearch || undefined,
        healthcareProfessionalTypeIds:
          selectedProviderTypes.length > 0
            ? selectedProviderTypes.map((pt) => pt.id)
            : undefined,
        state:
          selectedState && selectedState !== ALL_STATES_VALUE
            ? selectedState
            : undefined,
        licenseState:
          selectedLicenseState && selectedLicenseState !== ALL_STATES_VALUE
            ? selectedLicenseState
            : undefined,
        specialtyCodes:
          selectedSpecialties.length > 0 ? selectedSpecialties : undefined,
        source:
          selectedSource && selectedSource !== ALL_SOURCES_VALUE
            ? selectedSource
            : undefined,
      }),
    onSuccess: (data) => {
      // Convert to CSV
      const headers = [
        "ID",
        "First Name",
        "Last Name",
        "Middle Name",
        "Suffix",
        "Email",
        "Phone",
        "NPI",
        "Date of Birth",
        "City",
        "State",
        "Zip Code",
        "Provider Type",
        "Specialties",
        "License States",
      ];

      const csvRows = [
        headers.join(","),
        ...data.rows.map((row) =>
          [
            row.id,
            row.firstName ?? "",
            row.lastName ?? "",
            row.middleName ?? "",
            row.suffixName ?? "",
            row.email ?? "",
            row.phone ?? "",
            row.npiNumber ?? "",
            row.dateOfBirth ?? "",
            row.city ?? "",
            row.state ?? "",
            row.zipCode ?? "",
            row.healthcareProfessionalType ?? "",
            `"${(row.specialties ?? "").replace(/"/g, '""')}"`,
            `"${(row.licenseStates ?? "").replace(/"/g, '""')}"`,
          ].join(","),
        ),
      ];

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `leads-export-${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Export Complete", {
        description: `Exported ${data.count} leads to CSV`,
      });
    },
    onError: (error) => {
      toast.error("Export Failed", {
        description:
          error instanceof Error ? error.message : "Failed to export leads",
      });
    },
  });

  const formatLeadName = (lead: LeadListItem) => {
    const parts = [lead.firstName, lead.middleName, lead.lastName].filter(
      Boolean,
    );
    if (lead.suffixName) {
      parts.push(lead.suffixName);
    }
    return parts.join(" ") || "Unknown";
  };

  const renderLeadRow = (lead: LeadListItem) => {
    const specialties = lead.specialties ?? [];
    const stateLicenses = lead.stateLicenses ?? [];
    const providerTypeName = lead.healthcareProfessionalType?.name;
    const location =
      lead.city && lead.state
        ? `${lead.city}, ${lead.state}`
        : lead.city || lead.state || null;

    return (
      <Card
        key={lead.id}
        className="rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all hover:border-blue-200/70 hover:shadow-lg cursor-pointer"
        onClick={() => setSelectedLeadId(lead.id)}
      >
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900 md:text-lg truncate">
                  {formatLeadName(lead)}
                </h3>
                {providerTypeName && (
                  <p className="text-sm text-slate-600">{providerTypeName}</p>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
              {lead.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  <span className="truncate max-w-[200px]">{lead.email}</span>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-slate-400" />
                  <span>{lead.phone}</span>
                </div>
              )}
              {lead.npiNumber && (
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  <span>NPI: {lead.npiNumber}</span>
                </div>
              )}
              {location && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-slate-400" />
                  <span>{location}</span>
                </div>
              )}
            </div>

            {(specialties.length > 0 || stateLicenses.length > 0) && (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {specialties.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {specialties.slice(0, 3).map((specialty) => (
                      <Badge
                        key={specialty.id}
                        variant="outline"
                        size="small"
                        className="border-emerald-100 bg-emerald-50 text-emerald-800"
                      >
                        {specialty.name}
                      </Badge>
                    ))}
                    {specialties.length > 3 && (
                      <Badge variant="secondary" size="small">
                        +{specialties.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
                {stateLicenses.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {stateLicenses.slice(0, 3).map((license, index) => (
                      <Badge
                        key={`${license.state}-${index}`}
                        variant="outline"
                        size="small"
                        className="border-blue-100 bg-blue-50 text-blue-800"
                      >
                        {license.state}
                      </Badge>
                    ))}
                    {stateLicenses.length > 3 && (
                      <Badge variant="secondary" size="small">
                        +{stateLicenses.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDetailPanel = () => {
    const lead = leadDetailQuery.data;

    if (leadDetailQuery.isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      );
    }

    if (!lead) {
      return (
        <div className="flex items-center justify-center h-full text-slate-500">
          Unable to load lead details
        </div>
      );
    }

    const fullName = [
      lead.firstName,
      lead.middleName,
      lead.lastName,
      lead.suffixName,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <ScrollArea className="h-full">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{fullName}</h2>
            {lead.healthcareProfessionalType?.name && (
              <p className="text-slate-600">
                {lead.healthcareProfessionalType.name}
              </p>
            )}
            {lead.deletedAt && (
              <Badge variant="destructive" className="mt-2">
                Deleted
              </Badge>
            )}
          </div>

          <Separator />

          {/* Contact Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Contact Information
            </h3>
            <div className="space-y-2 text-sm">
              {lead.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span>{lead.email}</span>
                  {getFieldSourceBadge(lead, "email")}
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <span>{lead.phone}</span>
                  {getFieldSourceBadge(lead, "phone")}
                </div>
              )}
              {lead.npiNumber && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span>NPI: {lead.npiNumber}</span>
                  {getFieldSourceBadge(lead, "npi_number")}
                </div>
              )}
              {lead.dateOfBirth && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span>DOB: {lead.dateOfBirth}</span>
                  {getFieldSourceBadge(lead, "date_of_birth")}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          {(lead.city || lead.state || lead.zipCode) && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Location
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span>
                    {[lead.city, lead.state, lead.zipCode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Specialties */}
          {lead.specialties && lead.specialties.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Specialties
                </h3>
                <div className="flex flex-wrap gap-2">
                  {lead.specialties.map((specialty) => (
                    <Badge
                      key={specialty.id}
                      variant="outline"
                      className="border-emerald-200 bg-emerald-50 text-emerald-800"
                    >
                      {specialty.name}
                      {specialty.code && (
                        <span className="text-emerald-600 ml-1">
                          ({specialty.code})
                        </span>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* State Licenses */}
          {lead.stateLicenses && lead.stateLicenses.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  State Licenses
                </h3>
                <div className="space-y-2">
                  {lead.stateLicenses.map((license) => (
                    <div
                      key={license.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-slate-50"
                    >
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={license.isActive ? "default" : "secondary"}
                        >
                          {license.state}
                        </Badge>
                        {license.licenseNumber && (
                          <span className="text-sm text-slate-600">
                            #{license.licenseNumber}
                          </span>
                        )}
                      </div>
                      {license.expirationDate && (
                        <span className="text-xs text-slate-500">
                          Expires: {license.expirationDate}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Facilities */}
          {lead.facilities && lead.facilities.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Facilities
                </h3>
                <div className="space-y-2">
                  {lead.facilities.map((facilityRecord) => (
                    <div
                      key={facilityRecord.id}
                      className="p-3 rounded-lg bg-slate-50 space-y-1"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-slate-900">
                          {facilityRecord.facility?.name ||
                            facilityRecord.facilityName ||
                            "Unknown Facility"}
                        </span>
                        {facilityRecord.isCurrent && (
                          <Badge size="small" variant="default">
                            Current
                          </Badge>
                        )}
                      </div>
                      {facilityRecord.facility && (
                        <p className="text-sm text-slate-600 ml-6">
                          {[
                            facilityRecord.facility.city,
                            facilityRecord.facility.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      )}
                      {(facilityRecord.startDate || facilityRecord.endDate) && (
                        <p className="text-xs text-slate-500 ml-6">
                          {facilityRecord.startDate || "?"} -{" "}
                          {facilityRecord.endDate || "Present"}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          {lead.notes && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Notes
                </h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">
                  {lead.notes}
                </p>
              </div>
            </>
          )}

          {/* Source Data */}
          {lead.sourceData && lead.sourceData.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Data Sources ({lead.sourceData.length})
                </h3>
                <div className="space-y-3">
                  {lead.sourceData.map((source) => (
                    <div
                      key={source.id}
                      className="p-3 rounded-lg border border-slate-200 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{source.source}</Badge>
                        <span className="text-xs text-slate-500">
                          {source.importedAt
                            ? formatDistanceToNow(new Date(source.importedAt), {
                                addSuffix: true,
                              })
                            : "Unknown date"}
                        </span>
                      </div>
                      {source.importedByUser && (
                        <p className="text-xs text-slate-500">
                          Imported by {source.importedByUser.firstName}{" "}
                          {source.importedByUser.lastName}
                        </p>
                      )}
                      {source.fileName && (
                        <p className="text-xs text-slate-500">
                          File: {source.fileName}
                        </p>
                      )}
                      <div className="text-xs text-slate-600 space-y-1">
                        {source.firstName && (
                          <p>
                            <span className="text-slate-400">Name:</span>{" "}
                            {[
                              source.firstName,
                              source.middleName,
                              source.lastName,
                              source.suffixName,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                        )}
                        {source.email && (
                          <p>
                            <span className="text-slate-400">Email:</span>{" "}
                            {source.email}
                          </p>
                        )}
                        {source.phone && (
                          <p>
                            <span className="text-slate-400">Phone:</span>{" "}
                            {source.phone}
                          </p>
                        )}
                        {source.npiNumber && (
                          <p>
                            <span className="text-slate-400">NPI:</span>{" "}
                            {source.npiNumber}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Metadata */}
          <Separator />
          <div className="space-y-2 text-xs text-slate-500">
            <p>Created: {format(new Date(lead.createdAt), "PPpp")}</p>
            <p>Updated: {format(new Date(lead.updatedAt), "PPpp")}</p>
            <p className="font-mono text-slate-400">ID: {lead.id}</p>
          </div>
        </div>
      </ScrollArea>
    );
  };

  const getFieldSourceBadge = (lead: LeadDetail, field: string) => {
    const fieldSource = lead.fieldSources?.find((fs) => fs.field === field);
    if (!fieldSource) {
      return null;
    }

    return (
      <Badge variant="outline" size="small" className="text-xs">
        {fieldSource.source}
      </Badge>
    );
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Leads</h1>
          <p className="text-sm text-slate-500">
            {leadsQuery.data?.pages[0]?.totalEstimate !== undefined ? (
              <>
                ~{leadsQuery.data.pages[0].totalEstimate.toLocaleString()} lead
                {leadsQuery.data.pages[0].totalEstimate === 1 ? "" : "s"}
              </>
            ) : leadsQuery.isLoading ? (
              "Loading..."
            ) : (
              <>
                {leads.length} lead{leads.length === 1 ? "" : "s"}
                {leadsQuery.hasNextPage && "+"}
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => exportMutation.mutate()}
            variant="outline"
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export CSV
          </Button>
          <Button
            onClick={() => leadsQuery.refetch()}
            variant="outline"
            disabled={leadsQuery.isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${leadsQuery.isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-3">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search by name, email, phone, or NPI..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery("")}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Provider Type */}
          <div className="w-full sm:w-56">
            <HealthcareProfessionalTypeSelect
              value={selectedProviderTypes}
              onChange={(value) =>
                setSelectedProviderTypes(
                  (value as Array<{ id: string; name: string }>) ?? [],
                )
              }
              placeholder="Provider type"
              isMulti
            />
          </div>

          {/* Location State */}
          <div className="w-full sm:w-40">
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="h-[50px]">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATES_VALUE}>All locations</SelectItem>
                {states?.map((state) => (
                  <SelectItem key={state.id} value={state.id}>
                    {state.displayText}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* License State */}
          <div className="w-full sm:w-40">
            <Select
              value={selectedLicenseState}
              onValueChange={setSelectedLicenseState}
            >
              <SelectTrigger className="h-[50px]">
                <SelectValue placeholder="License" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATES_VALUE}>All licenses</SelectItem>
                {licenseStates?.map((state) => (
                  <SelectItem key={state.id} value={state.id}>
                    {state.displayText}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Specialty */}
          <div className="w-full sm:w-56">
            <SpecialtiesSelect
              value={selectedSpecialties}
              onChange={(value) =>
                setSelectedSpecialties((value as string[]) ?? [])
              }
              placeholder="Specialty"
              isMulti
            />
          </div>

          {/* Source */}
          <div className="w-full sm:w-40">
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger className="h-[50px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SOURCES_VALUE}>All sources</SelectItem>
                {sources?.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    {source.displayText}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* Leads List */}
      <div className="space-y-4">
        {leadsQuery.isLoading && !leadsQuery.data ? (
          <Card className="border-dashed">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Loading leads</h3>
              <p className="text-gray-600 mb-6 max-w-md">
                Please wait while we fetch the leads.
              </p>
            </CardContent>
          </Card>
        ) : leadsQuery.isError ? (
          <Card className="border-dashed border-2 border-red-300 bg-red-50">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-red-100 p-3 rounded-full mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Failed to load leads
              </h3>
              <p className="text-gray-600 mb-6 max-w-md">
                {leadsQuery.error instanceof Error
                  ? leadsQuery.error.message
                  : "An error occurred while loading leads. Please try again."}
              </p>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => leadsQuery.refetch()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : leads.length > 0 ? (
          <>
            {leads.map(renderLeadRow)}

            {/* Load more trigger */}
            <div ref={loadMoreRef} className="py-4">
              {leadsQuery.isFetchingNextPage && (
                <div className="flex items-center justify-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading more...</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No leads found</h3>
              <p className="text-gray-600 max-w-md">
                {hasActiveFilters
                  ? "No leads match the current filters. Try adjusting your search criteria."
                  : "No leads have been imported yet."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Detail Panel */}
      <Sheet
        open={Boolean(selectedLeadId)}
        onOpenChange={(open) => !open && setSelectedLeadId(null)}
      >
        <SheetContent className="w-full sm:max-w-lg overflow-hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>Lead Details</SheetTitle>
            <SheetDescription>
              View complete information about this lead
            </SheetDescription>
          </SheetHeader>
          {renderDetailPanel()}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export const Route = createFileRoute("/_admin/admin/leads/")({
  component: AdminLeadsPage,
  validateSearch: z.object({
    search: z.string().optional(),
    healthcareProfessionalTypeIds: z.array(z.string().uuid()).optional(),
    state: z.string().optional(),
    licenseState: z.string().optional(),
    specialtyCodes: z.array(z.string()).optional(),
    source: z.string().optional(),
  }),
});
